/* BuyerLoans — Layer E (Buyer Portal · iter-90).

   The buyer's view of their credit-line activity:
     • Eligibility card (Allianz approval status, available limit)
     • Active loans roster (every open loan + due date + status)
     • Per-loan detail with Prepay action
     • Credit-line request wizard for buyers without a grant

   Theme: matches buyer portal (foreground/background slate palette).
*/
import { useEffect, useState } from "react";
import api, { money } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Badge } from "../../components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "../../components/ui/dialog";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "../../components/ui/select";
import { toast } from "sonner";
import {
  CreditCard, Calendar, Clock, ArrowRight, AlertTriangle, CheckCircle,
  Sparkles, Send, Wallet, ShieldCheck, FileText,
} from "lucide-react";


const STATUS_TONE = {
  active: "emerald", past_due: "amber",
  past_due_tier2: "orange", past_due_tier3: "rose",
  insurance_takeover: "violet", paid: "slate",
  nsf_pending: "rose", extended: "sky", void: "slate",
};

const fmtDate = (s) => { try { return new Date(s).toLocaleDateString(); } catch { return s || "—"; } };

function StatusPill({ status }) {
  const tone = STATUS_TONE[status] || "slate";
  return (
    <span className={`px-2 py-0.5 rounded-full text-[10px] bg-${tone}-50 text-${tone}-700 border border-${tone}-200`}>
      {status?.replace(/_/g, " ")}
    </span>
  );
}

export default function BuyerLoans() {
  const [elig, setElig] = useState(null);
  const [loans, setLoans] = useState([]);
  const [proposals, setProposals] = useState([]);
  const [open, setOpen] = useState(null);
  const [requestOpen, setRequestOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [e, l, p] = await Promise.all([
        api.get("/buyer/loans/eligibility"),
        api.get("/buyer/loans"),
        api.get("/buyer/credit-line/proposals"),
      ]);
      setElig(e.data); setLoans(l.data.rows || []); setProposals(p.data.rows || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  if (!elig) return <div className="p-12 text-foreground/40">Loading…</div>;

  const openLoans = loans.filter((l) => !["paid", "void", "insurance_takeover"].includes(l.status));
  const closedLoans = loans.filter((l) => ["paid", "void", "insurance_takeover"].includes(l.status));

  return (
    <div className="min-h-screen">
      <div className="page-shell" data-testid="buyer-loans">
        <div className="text-xs mono uppercase tracking-[0.2em] text-foreground/50">Buyer Portal · Credit Line</div>
        <div className="flex items-end justify-between mt-3 flex-wrap gap-4">
          <h1 className="display text-5xl tracking-[-0.02em] leading-[1.05]">
            Your credit line.
          </h1>
          {!elig.has_credit_grant && (
            <Button onClick={() => setRequestOpen(true)} className="rounded-full h-11 px-5" data-testid="bl-request-btn">
              <Send className="w-4 h-4 mr-1.5"/> Request credit line
            </Button>
          )}
        </div>

        {/* === Eligibility hero === */}
        {elig.has_credit_grant ? (
          <div className="mt-12 grid grid-cols-3 gap-px bg-border rounded-2xl overflow-hidden border border-border" data-testid="bl-elig-strip">
            <div className="bg-background p-7">
              <div className="text-xs mono uppercase tracking-[0.15em] text-foreground/50">Credit limit</div>
              <div className="display text-4xl mt-3 tracking-tight" data-testid="bl-limit">{money(elig.credit_limit_usd)}</div>
              <div className="text-xs text-foreground/55 mt-2">Approved by {elig.lender_org_name || "your lender"}</div>
            </div>
            <div className="bg-background p-7">
              <div className="text-xs mono uppercase tracking-[0.15em] text-foreground/50">Available</div>
              <div className="display text-4xl mt-3 tracking-tight" data-testid="bl-available">{money(elig.available_usd)}</div>
              <div className="text-xs text-foreground/55 mt-2">Utilized {money(elig.utilized_usd)}</div>
            </div>
            <div className="bg-background p-7">
              <div className="text-xs mono uppercase tracking-[0.15em] text-foreground/50">Active loans</div>
              <div className="display text-4xl mt-3 tracking-tight" data-testid="bl-open-count">{openLoans.length}</div>
              <div className="text-xs text-foreground/55 mt-2">{loans.length} total over your lifetime</div>
            </div>
          </div>
        ) : (
          <div className="mt-12 rounded-2xl border border-border p-10 text-center bg-secondary/20" data-testid="bl-no-grant">
            <Sparkles className="w-10 h-10 mx-auto text-foreground/40"/>
            <div className="display text-2xl tracking-tight mt-4">No credit line yet</div>
            <div className="text-sm text-foreground/55 mt-2 max-w-md mx-auto">
              Apply for a credit line and pay invoices via Net-14/30/60/90 terms. We run an Allianz Trade quote and route to a lender for approval.
            </div>
            <Button onClick={() => setRequestOpen(true)} className="mt-5 rounded-full h-10 px-5" data-testid="bl-request-btn-empty">
              Apply now <ArrowRight className="w-4 h-4 ml-1.5"/>
            </Button>
            {proposals.length > 0 && (
              <div className="mt-6 max-w-md mx-auto text-left rounded-xl border bg-background p-4" data-testid="bl-proposals">
                <div className="text-xs mono uppercase tracking-[0.15em] text-foreground/50 mb-2">Previous applications</div>
                {proposals.slice(0, 3).map((p) => (
                  <div key={p.id} className="flex items-center justify-between py-1.5 text-sm">
                    <div>
                      <div>{money(p.requested_limit_usd)} requested</div>
                      <div className="text-[10px] text-foreground/50">{fmtDate(p.created_at)}</div>
                    </div>
                    <Badge variant="outline">{p.status?.replace(/_/g, " ")}</Badge>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* === Open loans === */}
        {openLoans.length > 0 && (
          <div className="mt-14">
            <div className="text-xs mono uppercase tracking-[0.15em] text-foreground/50 mb-5">Open loans · {openLoans.length}</div>
            <div className="divide-y divide-border border-y border-border" data-testid="bl-open-loans">
              {openLoans.map((l) => {
                const pendingShip = String(l.status || "").includes("pending_ship");
                const anchorShip = (l.terms_start_event || "ship_date") === "ship_date";
                return (
                <button key={l.id} onClick={() => setOpen(l)} className="w-full text-left py-5 grid grid-cols-12 gap-3 items-center text-sm hover:bg-secondary/30" data-testid={`bl-loan-${l.id}`}>
                  <div className="col-span-3">
                    <div className="font-mono text-xs">{l.loan_number}</div>
                    <div className="text-[10px] text-foreground/50 mt-0.5">{l.invoice_code}</div>
                  </div>
                  <div className="col-span-2 text-xs">
                    {l.terms?.toUpperCase()}
                    {anchorShip && (pendingShip ? (
                      <span data-testid={`bl-clock-pending-${l.id}`} className="mt-1 flex items-center gap-1 text-[9.5px] text-amber-700">
                        <Clock className="w-2.5 h-2.5"/>Starts on ship
                      </span>
                    ) : (
                      <span data-testid={`bl-clock-running-${l.id}`} className="mt-1 flex items-center gap-1 text-[9.5px] text-emerald-700">
                        <Clock className="w-2.5 h-2.5"/>Clock running
                      </span>
                    ))}
                  </div>
                  <div className="col-span-2 tabular-nums text-base">{money(l.total_owed)}</div>
                  <div className="col-span-3"><StatusPill status={l.status}/></div>
                  <div className="col-span-2 text-right">
                    <div className="text-xs">{pendingShip ? <span className="text-amber-700">Awaiting carrier</span> : fmtDate(l.due_date)}</div>
                    {l.days_past_due > 0 && <div className="text-[10px] text-rose-600">{l.days_past_due}d past due</div>}
                  </div>
                </button>
              );})}
            </div>
          </div>
        )}

        {/* === Closed loans === */}
        {closedLoans.length > 0 && (
          <div className="mt-14">
            <div className="text-xs mono uppercase tracking-[0.15em] text-foreground/50 mb-5">Closed loans · {closedLoans.length}</div>
            <div className="divide-y divide-border border-y border-border" data-testid="bl-closed-loans">
              {closedLoans.slice(0, 20).map((l) => (
                <div key={l.id} className="py-4 grid grid-cols-12 gap-3 items-center text-sm">
                  <div className="col-span-3 font-mono text-xs">{l.loan_number}</div>
                  <div className="col-span-2 text-xs">{l.terms?.toUpperCase()}</div>
                  <div className="col-span-2 tabular-nums text-foreground/55">{money(l.principal)}</div>
                  <div className="col-span-3"><StatusPill status={l.status}/></div>
                  <div className="col-span-2 text-right text-xs text-foreground/55">{fmtDate(l.paid_at || l.opened_at)}</div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <LoanDetailDialog loan={open} onClose={() => setOpen(null)} onPaid={load}/>
      <RequestCreditLineDialog open={requestOpen} onClose={() => setRequestOpen(false)} onSubmitted={() => { setRequestOpen(false); load(); }}/>
    </div>
  );
}


function LoanDetailDialog({ loan, onClose, onPaid }) {
  const [data, setData] = useState(null);
  const [history, setHistory] = useState(null);
  const [prepayOpen, setPrepayOpen] = useState(false);
  const [partial, setPartial] = useState("");
  const [partialMode, setPartialMode] = useState("full");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!loan) { setData(null); setHistory(null); return; }
    Promise.all([
      api.get(`/buyer/loans/${loan.id}`),
      api.get(`/buyer/loans/${loan.id}/repayment-attempts`),
    ]).then(([d, h]) => { setData(d.data); setHistory(h.data); });
  }, [loan?.id]);

  if (!loan) return null;

  const closable = data && !["paid", "void", "insurance_takeover"].includes(data.status);

  const doPrepay = async () => {
    setBusy(true);
    try {
      const body = partialMode === "full" ? {} : { amount: Number(partial) };
      const { data: res } = await api.post(`/buyer/loans/${loan.id}/prepay`, body);
      toast.success(res.outcome === "paid" ? "Loan settled" : `Paid ${money(res.amount_paid)} — ${money(res.remaining)} remaining`);
      setPrepayOpen(false); setPartial(""); setPartialMode("full");
      onPaid?.(); onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail?.reason || "Prepay failed");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={!!loan} onOpenChange={onClose}>
      <DialogContent data-testid="bl-loan-detail" className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="font-mono text-base">{loan.loan_number}</DialogTitle>
          <DialogDescription>Invoice {loan.invoice_code} · {loan.terms?.toUpperCase()}</DialogDescription>
        </DialogHeader>
        {data && (
          <div className="space-y-4">
            <div className="grid grid-cols-3 gap-3">
              <div className="rounded-lg border bg-secondary/20 px-4 py-3">
                <div className="text-[10px] uppercase text-foreground/50">Total owed</div>
                <div className="display text-2xl tabular-nums mt-1">{money(data.total_owed)}</div>
              </div>
              <div className="rounded-lg border bg-secondary/20 px-4 py-3">
                <div className="text-[10px] uppercase text-foreground/50">Status</div>
                <div className="mt-2"><StatusPill status={data.status}/></div>
                {data.days_past_due > 0 && <div className="text-[10px] text-rose-600 mt-1">{data.days_past_due}d past due</div>}
              </div>
              <div className="rounded-lg border bg-secondary/20 px-4 py-3">
                <div className="text-[10px] uppercase text-foreground/50">Due</div>
                <div className="text-sm mt-2">{fmtDate(data.due_date)}</div>
                <div className="text-[10px] text-foreground/55 mt-0.5">{data.fee_owed > 0 && `Fee ${money(data.fee_owed)}`}</div>
              </div>
            </div>

            {history && history.repayment_attempts?.length > 0 && (
              <div>
                <div className="text-sm font-medium text-foreground/70 mb-1.5">Repayment attempts</div>
                <div className="rounded-lg border divide-y max-h-32 overflow-auto" data-testid="bl-attempts">
                  {history.repayment_attempts.map((a, i) => (
                    <div key={i} className="px-3 py-2 text-xs grid grid-cols-12 gap-2">
                      <div className="col-span-5 text-foreground/55">{new Date(a.attempted_at).toLocaleString()}</div>
                      <div className="col-span-3 tabular-nums">{money(a.amount_paid ?? a.amount_due)}</div>
                      <div className="col-span-4 text-right">
                        <span className={`px-2 py-0.5 rounded-full text-[10px] ${a.outcome === "paid" ? "bg-emerald-50 text-emerald-700" : a.outcome === "partial" ? "bg-amber-50 text-amber-700" : "bg-rose-50 text-rose-700"}`}>{a.outcome}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {data.status === "insurance_takeover" && (
              <div className="rounded-lg border border-violet-200 bg-violet-50 px-4 py-3 text-sm" data-testid="bl-takeover-notice">
                <div className="flex items-center gap-2 text-violet-700 font-medium"><ShieldCheck className="w-4 h-4"/> This loan has been handed to Allianz Trade.</div>
                <div className="text-xs text-violet-600 mt-1.5">You owe Allianz directly from this point. Claim ID: <span className="font-mono">{data.insurance_claim_id}</span></div>
              </div>
            )}
          </div>
        )}
        <DialogFooter>
          {closable && (
            <Button onClick={() => setPrepayOpen(true)} variant="outline" data-testid="bl-prepay-btn">
              <Wallet className="w-4 h-4 mr-1.5"/> Prepay
            </Button>
          )}
          <Button onClick={onClose}>Close</Button>
        </DialogFooter>

        {/* Prepay sub-dialog */}
        <Dialog open={prepayOpen} onOpenChange={setPrepayOpen}>
          <DialogContent data-testid="bl-prepay-dialog">
            <DialogHeader>
              <DialogTitle>Prepay loan</DialogTitle>
              <DialogDescription>Pull funds from your wallet to settle (or partially settle) this loan.</DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <div>
                <Label>Pay</Label>
                <Select value={partialMode} onValueChange={setPartialMode}>
                  <SelectTrigger data-testid="bl-prepay-mode"><SelectValue/></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="full">Full balance ({money(data?.total_owed)})</SelectItem>
                    <SelectItem value="partial">Partial amount</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {partialMode === "partial" && (
                <div><Label>Amount (USD)</Label><Input type="number" value={partial} onChange={(e) => setPartial(e.target.value)} data-testid="bl-prepay-amount"/></div>
              )}
              <div className="text-xs text-foreground/55">No prepay discount — you owe the full fee regardless of timing.</div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setPrepayOpen(false)}>Cancel</Button>
              <Button onClick={doPrepay} disabled={busy} data-testid="bl-prepay-submit">{busy ? "Processing…" : "Confirm prepay"}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </DialogContent>
    </Dialog>
  );
}


function RequestCreditLineDialog({ open, onClose, onSubmitted }) {
  const [amount, setAmount] = useState(25000);
  const [terms, setTerms] = useState("net30");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/buyer/credit-line/request", {
        requested_limit_usd: Number(amount), default_terms: terms,
      });
      setResult(data);
      if (data.status === "rejected_by_insurance") {
        toast.error("Allianz Trade declined underwriting");
      } else {
        toast.success("Request submitted — awaiting lender approval");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail?.reason || "Request failed");
    } finally { setBusy(false); }
  };

  const close = () => { setResult(null); onSubmitted?.(); };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent data-testid="bl-request-dialog">
        <DialogHeader>
          <DialogTitle>Request a credit line</DialogTitle>
          <DialogDescription>We'll run an Allianz Trade quote and route to a lender for approval. Approval usually takes 24-48 hours.</DialogDescription>
        </DialogHeader>
        {!result ? (
          <>
            <div className="space-y-3">
              <div><Label>Requested limit (USD)</Label><Input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="bl-req-amount"/></div>
              <div>
                <Label>Default payment terms</Label>
                <Select value={terms} onValueChange={setTerms}>
                  <SelectTrigger data-testid="bl-req-terms"><SelectValue/></SelectTrigger>
                  <SelectContent>
                    {["net14", "net30", "net60", "net90"].map((t) => <SelectItem key={t} value={t}>{t.toUpperCase()}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={onClose}>Cancel</Button>
              <Button onClick={submit} disabled={busy || amount <= 0} data-testid="bl-req-submit">{busy ? "Submitting…" : "Submit application"}</Button>
            </DialogFooter>
          </>
        ) : (
          <>
            {result.status === "rejected_by_insurance" ? (
              <div className="text-center py-6">
                <AlertTriangle className="w-12 h-12 mx-auto text-rose-500"/>
                <div className="display text-xl mt-3">Application declined</div>
                <div className="text-sm text-foreground/55 mt-2 max-w-sm mx-auto">{result.reason}</div>
              </div>
            ) : (
              <div className="text-center py-6" data-testid="bl-req-pending">
                <CheckCircle className="w-12 h-12 mx-auto text-emerald-500"/>
                <div className="display text-xl mt-3">Application submitted</div>
                <div className="text-sm text-foreground/55 mt-2 max-w-sm mx-auto">A lender will review your request and decide within 24-48 hours.</div>
                {result.allianz_grade && (
                  <div className="mt-4 inline-flex items-center gap-2 rounded-full bg-secondary/40 px-3 py-1 text-xs">
                    Allianz Trade grade: <strong>{result.allianz_grade}</strong> · outlook {result.allianz_outlook}
                  </div>
                )}
              </div>
            )}
            <DialogFooter>
              <Button onClick={close}>Done</Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
